import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { pricingRules, managers } from '../data/dummyData';

const PricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const furniture = location.state?.furniture || { name: '소파 (3인용)', weight: '35~55kg', twoPersonRequired: true };
  const serviceType = location.state?.serviceType || 'verify_delivery';
  const hasElevator = location.state?.hasElevator !== undefined ? location.state?.hasElevator : true;
  const floor = location.state?.floor || 1;
  const sellerFloor = location.state?.sellerFloor || 2;
  const sellerElevator = location.state?.sellerElevator !== undefined ? location.state?.sellerElevator : false;

  const [fromAddress, setFromAddress] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [distance, setDistance] = useState(0);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [addWashing, setAddWashing] = useState(false);
  const [addAssembly, setAddAssembly] = useState(false);
  const [isHeavy, setIsHeavy] = useState(false);

  const sampleRoutes = [
    { from: '서울 마포구 합정동', to: '서울 은평구 응암동', km: 4.2 },
    { from: '서울 강남구 역삼동', to: '서울 마포구 상암동', km: 12.5 },
    { from: '서울 송파구 잠실동', to: '경기 성남시 분당구', km: 8.1 },
    { from: '서울 서대문구 신촌동', to: '서울 용산구 이태원동', km: 5.7 },
  ];

  const calculateTotal = (km) => {
    let base = pricingRules.base;
    let distanceFee = Math.ceil(km) * pricingRules.perKm;
    let elevatorPickup = sellerElevator ? pricingRules.elevatorFee : (parseInt(sellerFloor) - 1) * pricingRules.stairFee.perFloor;
    let elevatorDeliver = hasElevator ? pricingRules.elevatorFee : (parseInt(floor) - 1) * pricingRules.stairFee.perFloor;
    let heavyFee = isHeavy ? pricingRules.heavyItemFee : 0;
    let twoPerson = furniture.twoPersonRequired ? 5000 : 0;
    let verifyFee = (serviceType === 'verify' || serviceType === 'verify_delivery') ? pricingRules.verificationFee : 0;
    let washFee = addWashing ? pricingRules.washingFee : 0;
    let assemblyFee = addAssembly ? pricingRules.assemblyFee : 0;

    let total = base + distanceFee + elevatorPickup + elevatorDeliver + heavyFee + twoPerson + verifyFee + washFee + assemblyFee;

    return {
      base,
      distanceFee,
      elevatorPickup,
      elevatorDeliver,
      heavyFee,
      twoPerson,
      verifyFee,
      washFee,
      assemblyFee,
      total,
      originalTotal: Math.round(total * 1.35),
    };
  };

  const handleCalculate = (km) => {
    const d = km || distance;
    if (!d) return;
    const result = calculateTotal(parseFloat(d));
    setCalculatedPrice(result);
  };

  const handleSampleRoute = (route) => {
    setFromAddress(route.from);
    setToAddress(route.to);
    setDistance(route.km);
    const result = calculateTotal(route.km);
    setCalculatedPrice(result);
  };

  // 자동 계산
  useEffect(() => {
    if (distance > 0) handleCalculate(distance);
  }, [addWashing, addAssembly, isHeavy, distance]);

  return (
    <div className="page-white">
      {/* 헤더 */}
      <div style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C5A 100%)', padding: '20px 20px 24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 10, padding: '8px 12px', color: 'white', cursor: 'pointer', marginBottom: 12 }}
        >← 뒤로</button>
        <div style={{ color: 'white', fontSize: 22, fontWeight: 800, marginBottom: 4 }}>💰 투명 요금 계산</div>
        <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>숨겨진 비용 없는 투명한 요금</div>
      </div>

      <div style={{ padding: '20px 20px 100px' }}>
        {/* 상품 정보 요약 */}
        <div style={{ background: '#FFF0EB', borderRadius: 16, padding: 16, marginBottom: 20, border: '1px solid #FFD0B5' }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#FF6B35', marginBottom: 6 }}>🛋️ 배송 상품</div>
          <div style={{ fontSize: 14, color: '#1F2937', fontWeight: 600 }}>{furniture.name}</div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
            무게: {furniture.weight} | 크기: {furniture.size || '측정 중'}
          </div>
        </div>

        {/* 위치 입력 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937', marginBottom: 10 }}>📍 이동 경로 입력</div>

          {/* 샘플 경로 */}
          <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>빠른 선택:</div>
          <div className="scroll-x" style={{ marginBottom: 10 }}>
            {sampleRoutes.map((r, i) => (
              <button
                key={i}
                onClick={() => handleSampleRoute(r)}
                style={{
                  minWidth: 180, padding: '10px 14px', background: 'white',
                  border: `2px solid ${fromAddress === r.from ? '#FF6B35' : '#E5E7EB'}`,
                  borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: 11, color: '#6B7280' }}>📦 {r.from}</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>🏠 {r.to}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#FF6B35', marginTop: 4 }}>{r.km}km</div>
              </button>
            ))}
          </div>

          <div className="input-group">
            <label className="input-label">📦 판매자 주소</label>
            <input
              className="input-field"
              value={fromAddress}
              onChange={e => setFromAddress(e.target.value)}
              placeholder="예: 서울 마포구 합정동"
            />
          </div>

          <div className="input-group">
            <label className="input-label">🏠 배송 주소</label>
            <input
              className="input-field"
              value={toAddress}
              onChange={e => setToAddress(e.target.value)}
              placeholder="예: 서울 은평구 응암동"
            />
          </div>

          <div className="input-group">
            <label className="input-label">📏 예상 거리 (km)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="input-field"
                type="number"
                value={distance}
                onChange={e => setDistance(e.target.value)}
                placeholder="예: 4.2"
                style={{ flex: 1 }}
              />
              <button
                onClick={() => handleCalculate()}
                disabled={!distance}
                style={{
                  background: distance ? '#FF6B35' : '#E5E7EB',
                  color: distance ? 'white' : '#9CA3AF',
                  border: 'none', borderRadius: 10, padding: '0 16px',
                  fontWeight: 700, cursor: distance ? 'pointer' : 'not-allowed', fontSize: 14
                }}
              >
                계산
              </button>
            </div>
          </div>
        </div>

        {/* 추가 옵션 */}
        <div style={{ background: '#F9FAFB', borderRadius: 16, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937', marginBottom: 12 }}>⚙️ 추가 옵션</div>

          {[
            { key: 'washing', label: '🧹 가구 기본 세척', desc: '배송 전 기본 세척 서비스', price: '10,000원', val: addWashing, setter: setAddWashing },
            { key: 'assembly', label: '🔧 가구 조립 서비스', desc: '배송 후 기본 조립', price: '20,000원', val: addAssembly, setter: setAddAssembly },
            { key: 'heavy', label: '⚖️ 무거운 짐 (50kg↑)', desc: '2인 작업 추가 비용', price: '5,000원', val: isHeavy, setter: setIsHeavy },
          ].map(opt => (
            <div
              key={opt.key}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 0', borderBottom: '1px solid #E5E7EB'
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#1F2937' }}>{opt.label}</div>
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>{opt.desc}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#FF6B35' }}>+{opt.price}</span>
                <div
                  onClick={() => opt.setter(!opt.val)}
                  style={{
                    width: 44, height: 24, borderRadius: 12,
                    background: opt.val ? '#FF6B35' : '#E5E7EB',
                    position: 'relative', cursor: 'pointer', transition: 'background 0.2s'
                  }}
                >
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', background: 'white',
                    position: 'absolute', top: 2, transition: 'left 0.2s',
                    left: opt.val ? 22 : 2, boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
                  }}/>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 요금 계산 결과 */}
        {calculatedPrice && (
          <div>
            {/* 총액 강조 */}
            <div style={{
              background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C5A 100%)',
              borderRadius: 16, padding: '24px 20px', marginBottom: 16,
              textAlign: 'center', color: 'white'
            }}>
              <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 4 }}>예상 총 요금</div>
              <div style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-1px' }}>
                {calculatedPrice.total.toLocaleString()}원
              </div>
              <div style={{ fontSize: 13, opacity: 0.7, textDecoration: 'line-through', marginTop: 4 }}>
                기존 업체 평균 {calculatedPrice.originalTotal.toLocaleString()}원
              </div>
              <div style={{
                display: 'inline-block', background: 'rgba(255,255,255,0.2)',
                borderRadius: 20, padding: '4px 16px', fontSize: 12, fontWeight: 700, marginTop: 8
              }}>
                🎉 {Math.round((1 - calculatedPrice.total / calculatedPrice.originalTotal) * 100)}% 저렴
              </div>
            </div>

            {/* 요금 상세 내역 */}
            <div style={{ background: 'white', borderRadius: 16, padding: 20, marginBottom: 16, border: '1px solid #F3F4F6' }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937', marginBottom: 14 }}>
                📋 요금 상세 내역
              </div>
              {[
                { label: '기본 요금', amount: calculatedPrice.base, show: true },
                { label: `거리 요금 (${distance}km × 2,000원)`, amount: calculatedPrice.distanceFee, show: true },
                { label: `픽업 층수 ${sellerElevator ? '엘리베이터' : `계단 ${sellerFloor}층`}`, amount: calculatedPrice.elevatorPickup, show: calculatedPrice.elevatorPickup > 0 },
                { label: `배달 층수 ${hasElevator ? '엘리베이터' : `계단 ${floor}층`}`, amount: calculatedPrice.elevatorDeliver, show: calculatedPrice.elevatorDeliver > 0 },
                { label: '무거운 짐 추가 비용', amount: calculatedPrice.heavyFee, show: calculatedPrice.heavyFee > 0 },
                { label: '2인 작업 비용', amount: calculatedPrice.twoPerson, show: calculatedPrice.twoPerson > 0 },
                { label: '현장 검증비', amount: calculatedPrice.verifyFee, show: calculatedPrice.verifyFee > 0 },
                { label: '가구 세척 서비스', amount: calculatedPrice.washFee, show: calculatedPrice.washFee > 0 },
                { label: '가구 조립 서비스', amount: calculatedPrice.assemblyFee, show: calculatedPrice.assemblyFee > 0 },
              ].filter(item => item.show).map((item, i) => (
                <div key={i} className="info-row">
                  <span className="info-key">{item.label}</span>
                  <span className="info-val">+{item.amount.toLocaleString()}원</span>
                </div>
              ))}
              <div style={{ borderTop: '2px solid #1F2937', marginTop: 12, paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 800, fontSize: 16, color: '#1F2937' }}>합계</span>
                <span style={{ fontWeight: 800, fontSize: 22, color: '#FF6B35' }}>{calculatedPrice.total.toLocaleString()}원</span>
              </div>
            </div>

            {/* 검증비 안내 */}
            {(serviceType === 'verify' || serviceType === 'verify_delivery') && (
              <div style={{ background: '#e8f8ef', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#27AE60', marginBottom: 6 }}>
                  ✅ 검증 서비스 안내
                </div>
                <div style={{ fontSize: 13, color: '#166534', lineHeight: 1.6 }}>
                  현장 방문 후 상품에 하자가 있어 구매를 취소하실 경우,<br/>
                  <strong>검증비 15,000원만</strong> 지불하시면 됩니다.<br/>
                  배송비는 전액 환불됩니다. 🎉
                </div>
              </div>
            )}

            {/* 주변 매니저 추천 */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937', marginBottom: 10 }}>
                🧑 추천 매니저
              </div>
              {managers.filter(m => m.status === 'online').slice(0, 2).map(mgr => (
                <div
                  key={mgr.id}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px', background: 'white', borderRadius: 12,
                    border: '2px solid #E5E7EB', marginBottom: 8, cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => navigate('/booking', { state: { manager: mgr, price: calculatedPrice, furniture, serviceType } })}
                >
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#FFF0EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                      {mgr.emoji}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1F2937' }}>{mgr.name} {mgr.verificationBadge && <span style={{ background: '#FF6B35', color: 'white', padding: '1px 6px', borderRadius: 4, fontSize: 10 }}>인증</span>}</div>
                      <div style={{ fontSize: 12, color: '#9CA3AF' }}>⭐ {mgr.rating} • {mgr.distance}km • 지금 가능</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#FF6B35' }}>선택 →</div>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/booking', { state: { price: calculatedPrice, furniture, serviceType } })}
              style={{
                width: '100%', padding: '16px', borderRadius: 16,
                background: '#FF6B35', color: 'white', border: 'none',
                fontWeight: 800, fontSize: 16, cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(255,107,53,0.4)'
              }}
            >
              📅 {calculatedPrice.total.toLocaleString()}원으로 예약하기 →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingPage;
